import { useFormik } from 'formik'
import { useEffect } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import * as Yup from 'yup'

import { Accordion, Select, Textarea } from '@loadsmart/loadsmart-ui'
import { Dialog, Layout, Text, Button, TextField, Link, Field } from '@loadsmart/miranda-react'
import { toCSSValue } from '@loadsmart/miranda-react/dist/tokens'

import {
  ACTIVE_APPOINTMENT_STATUSES,
  RESCHEDULABLE_APPOINTMENT_STATUSES,
  type StopAppointment,
} from 'core/appointments/domain/Appointment'
import { RequestStatus } from 'core/domain/Request'
import { Icon } from 'core/ui/components/icon/Icon'
import { usePasteFromClipboard } from 'core/ui/hooks/usePasteFromClipboard'

import { useConfigContext } from 'config/ui/ConfigContext'

import { getOpenDockInitialValuesFromAppointment } from '../../domain/Appointment'
import { type AppointmentCommonFormValues } from '../../infra/ShipmentRepository'
import { APPOINTMENT_MISSCHEDULING_REASON_OPTIONS } from '../../presenters/appointment'
import { formatAppointmentURL } from '../../presenters/appointmentPresenter'
import { downloadBOL } from '../../use-cases/downloadBOL'
import openDockAppointmentImg from '../assets/opendock-appointment.jpg'
import { useUpdateOpenDockAppointment } from '../hooks/useUpdateOpenDockAppointment'
import { OpenDockAppointmentScheduledDrawer } from '../OpenDockAppointmentScheduledDrawer/OpenDockAppointmentScheduledDrawer'
import { ScheduleBanner } from './AppointmentBanner'

export function getValidationSchema(requireMissScheduling = false) {
  if (!requireMissScheduling) {
    return Yup.object().shape({
      confirmationNumber: Yup.string().required('Confirmation number is required'),
    })
  }

  return Yup.object().shape({
    confirmationNumber: Yup.string().required('Confirmation number is required'),
    misschedulingReason: Yup.object().shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    }),
    misschedulingNote: Yup.string()
      .nullable()
      .when('misschedulingReason.value', {
        is: 'other',
        then: () => Yup.string().required('Must provide a note'),
      }),
  })
}

const Bordered = styled.div`
  border-right: 1px solid ${toCSSValue('color-divider')};
  padding-right: ${toCSSValue('spacing-10')};
`

const List = styled.ul`
  color: ${toCSSValue('color-text-secondary')};
  margin: 0;
  padding: 0;
  padding-left: ${toCSSValue('spacing-6')};

  li {
    list-style: disc;
  }
`

const IconArrowsCollapse = styled(Icon)`
  transform: rotateZ(180deg);
`

function renderToggleIcon(expanded: boolean) {
  return expanded ? (
    <IconArrowsCollapse name="arrows-expanded" width={15} aria-label="" />
  ) : (
    <Icon name="arrows-expanded" width={15} aria-label="" />
  )
}

export type OpenDockAppointmentDialogProps = {
  isOpen: boolean
  onClose: () => void
  shipmentUUID: string
  appointment?: StopAppointment
  facilityName?: string
  warehouseUUID?: string | null
  stopIndex: number
  onConfirm: () => void
  stopDate?: string
  shipperEmail?: string
}

export function OpenDockAppointmentDialog({
  isOpen,
  onClose,
  shipmentUUID,
  appointment,
  stopIndex,
  facilityName,
  warehouseUUID,
  onConfirm,
  stopDate,
  shipperEmail,
}: OpenDockAppointmentDialogProps) {
  const config: Array<string> = useConfigContext()['enableNewOpenDockReschedulingDrawer'].toObject()

  const isShipperEnabledForNewOpenDockDrawer = config.includes(shipperEmail ?? '')

  const hasAppointment = appointment !== null && appointment !== undefined
  const isActiveAppointment = Boolean(
    appointment && ACTIVE_APPOINTMENT_STATUSES.includes(appointment.status)
  )
  const isReschedulable = Boolean(
    appointment && RESCHEDULABLE_APPOINTMENT_STATUSES.includes(appointment.status)
  )
  const missSchedulingIsRequired = hasAppointment && isReschedulable

  const initialValues = getOpenDockInitialValuesFromAppointment(appointment)
  const validationSchema = getValidationSchema(missSchedulingIsRequired)
  const scheduleDate = appointment?.suggestedDate?.start || stopDate

  const { handleChange, setFieldValue, values, isValid, isSubmitting, dirty } =
    useFormik<AppointmentCommonFormValues>({
      initialValues,
      validationSchema,
      onSubmit: () => undefined,
      enableReinitialize: true,
    })
  const disableSubmit = !isValid || isSubmitting || !dirty

  const { paste } = usePasteFromClipboard({
    onSuccess: pastedText => {
      setFieldValue('confirmationNumber', pastedText)
      toast.success('Confirmation number pasted!')
    },
  })

  const { update: updateAppointment, requestStatus } = useUpdateOpenDockAppointment()

  async function onSubmit() {
    await updateAppointment({
      shipmentUUID,
      stopIndex,
      data: values,
    })
  }

  useEffect(() => {
    if (requestStatus === RequestStatus.Done) {
      onConfirm()
      toast.success('Appointment updated successfully')
    }
  }, [onConfirm, requestStatus])

  useEffect(() => {
    if (requestStatus === RequestStatus.Error) {
      toast.error('Could not update appointment, try again later')
    }
  }, [requestStatus])

  function handleOpenSchedule() {
    const openDockScheduleURL = formatAppointmentURL(appointment, warehouseUUID)
    window.open(openDockScheduleURL, '_blank')
  }

  async function handleDownloadBOL() {
    const data = await downloadBOL(shipmentUUID)
    const fileURL = window.URL.createObjectURL(data)
    window.open(fileURL, '_blank')
  }

  if (isShipperEnabledForNewOpenDockDrawer) {
    return (
      <OpenDockAppointmentScheduledDrawer
        isOpen={isOpen}
        onClose={onClose}
        appointment={appointment}
      />
    )
  }

  return (
    <Dialog
      open={isOpen}
      size="medium"
      onClose={onClose}
      data-testid="opendock-appointment-dialog"
      style={{ marginTop: 0 }}
    >
      <Dialog.Header>
        Appointment
        <Dialog.Close />
      </Dialog.Header>
      <Dialog.Body>
        <Layout.Stack gap="spacing-4">
          {!isActiveAppointment && (
            <ScheduleBanner scheduleAt={scheduleDate} facilityName={facilityName} />
          )}
          <Layout.Group>
            <Bordered>
              <Layout.Stack gap="spacing-2">
                <Layout.Stack gap="none">
                  <Text variant="body-sm-bold" color="color-text-link">
                    Start Here
                  </Text>
                  <Text variant="body-sm" color="color-text-secondary">
                    Go to Opendock
                  </Text>
                </Layout.Stack>
                <Button type="button" variant="secondary" size="small" onClick={handleOpenSchedule}>
                  Open schedule
                </Button>
              </Layout.Stack>
            </Bordered>

            <Layout.Stack gap="spacing-2" style={{ flex: 1 }}>
              <Layout.Stack gap="none">
                <Text variant="body-sm-bold" color="color-text-link" id="synchronize">
                  Synchronize
                </Text>
                <Text variant="body-sm" color="color-text-secondary">
                  Paste the confirmation number in the field below
                </Text>
              </Layout.Stack>
              <Layout.Group align="center">
                <TextField
                  size="small"
                  style={{ flex: 1 }}
                  value={values.confirmationNumber || undefined}
                  data-testid="confirmation-number-field"
                  id="confirmationNumber"
                  onInput={handleChange}
                />
                <Button
                  type="button"
                  variant="tertiary"
                  size="small"
                  onClick={() => {
                    paste()
                  }}
                >
                  Paste number
                </Button>
              </Layout.Group>
            </Layout.Stack>
          </Layout.Group>

          <Layout.Stack gap="spacing-0-5">
            <Accordion>
              <Accordion.Toggle trailing={renderToggleIcon}>
                <Text color="color-text-secondary" variant="body-md-bold">
                  Need help?
                </Text>
              </Accordion.Toggle>
              <Accordion.Body>
                <List>
                  <li>
                    Click in the &quot;Open schedule&quot; button and login to Opendock to schedule
                    an appointment in the {`${facilityName}`}.
                  </li>
                  <li>
                    Choose an appointment time and complete the requested data using the
                    <Link
                      href="#"
                      onClick={() => {
                        handleDownloadBOL()
                      }}
                    >
                      BOL information (download here).
                    </Link>
                  </li>
                  <li>
                    Copy the <span style={{ textTransform: 'uppercase' }}>CONFIRMATION NUMBER</span>{' '}
                    received from Opendock after scheduling an appointment and paste it in the field
                    above.
                  </li>
                </List>
                <img
                  src={openDockAppointmentImg}
                  alt="opendock appointment hint"
                  style={{ maxWidth: '240px' }}
                />
              </Accordion.Body>
            </Accordion>
          </Layout.Stack>

          {hasAppointment && isReschedulable && (
            <Layout.Stack gap="spacing-0-5">
              <Text color="color-warning-100" variant="body-sm">
                To review or reschedule this appointment go to Opendock. In case of reschedule
                please fill the reason below.
              </Text>
              <Layout.Group gap="spacing-2">
                <Field required>
                  <Field.Label color="color-text-tertiary">Reschedule reason</Field.Label>
                  <Select
                    name="misschedulingReason"
                    placeholder="Select reason"
                    options={APPOINTMENT_MISSCHEDULING_REASON_OPTIONS}
                    value={values.misschedulingReason}
                    onChange={handleChange}
                    hideClear
                  />
                </Field>
                <Field
                  style={{ flex: 1 }}
                  label="Note (max 100 characters)"
                  required={values.misschedulingReason.value === 'other'}
                >
                  <Textarea
                    onInput={handleChange}
                    name="misschedulingNote"
                    value={values.misschedulingNote}
                    aria-label="Note"
                  />
                </Field>
              </Layout.Group>
            </Layout.Stack>
          )}
        </Layout.Stack>
      </Dialog.Body>
      <Dialog.Actions>
        <Dialog.ActionPrimary
          onClick={onSubmit}
          loading={requestStatus === RequestStatus.Pending}
          disabled={disableSubmit}
        >
          Finish and close
        </Dialog.ActionPrimary>
      </Dialog.Actions>
    </Dialog>
  )
}
